import Reveal from "@/components/Reveal";
import CountUp from "@/components/CountUp";
import AnimatedBar from "@/components/AnimatedBar";

const INTENT_PARTNERS = ["Bombora", "ZoomInfo", "6sense", "Lusha", "Cognism"];

const KEYSTATS = [
  { v: 46, suffix: "M", l: "Total contacts globally", decimals: 0 },
  { v: 96.1, suffix: "%", l: "Verified data accuracy", decimals: 1 },
  { v: 45, suffix: "+", l: "Industries reached", decimals: 0 },
  { v: 33, suffix: "K+", l: "Technology keywords tracked", decimals: 0 },
];

const AUDIENCE = [
  { v: 31, l: "Business Leaders" },
  { v: 31, l: "IT Decision Makers" },
  { v: 22, l: "Technology Influencers" },
  { v: 16, l: "Technology Audience" },
];

export default function DataCoverage() {
  return (
    <section
      id="data"
      data-testid="data-coverage-section"
      className="relative py-24 md:py-32 border-t border-white/5 overflow-hidden"
    >
      {/* Ambient scan sweep */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 grid-bg opacity-25 [mask-image:radial-gradient(ellipse_at_top,black_20%,transparent_75%)]" />
        <div className="absolute left-0 right-0 top-0 h-40 data-sweep" />
      </div>

      <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-14">
          <Reveal className="lg:col-span-6">
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-teal mb-4">
              Data Intelligence
            </div>
            <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-[56px] leading-[1] tracking-[-0.02em]">
              <CountUp end={46} suffix="M" /> contacts.{" "}
              <span className="italic font-normal text-teal">Real-time signal.</span>
            </h2>
          </Reveal>
          <Reveal delay={200} className="lg:col-span-5 lg:col-start-8">
            <p className="text-base text-muted-foreground max-w-md">
              A proprietary data stack combining intent signals, install-base
              intelligence, and first-party engagement — refreshed continuously.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <Reveal className="lg:col-span-7">
            <div className="rounded-2xl border border-white/[0.06] bg-[#0A121A] p-8 md:p-10 relative overflow-hidden">
              {/* Radar-like sweep behind the big number */}
              <div className="absolute inset-0 pointer-events-none opacity-40">
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[380px] w-[380px] rounded-full border border-teal/10 radar-ring" />
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[280px] w-[280px] rounded-full border border-teal/10 radar-ring" style={{ animationDelay: "1.4s" }} />
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[180px] w-[180px] rounded-full border border-teal/15 radar-ring" style={{ animationDelay: "2.8s" }} />
              </div>

              <div className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground mb-6">
                Total Addressable Contacts · Globally
              </div>
              <div className="font-display text-6xl md:text-7xl font-semibold tracking-tighter">
                ~<CountUp end={46} suffix="M" />
              </div>

              <div className="mt-10 grid grid-cols-2 gap-4">
                <Reveal delay={200}>
                  <div className="rounded-xl border border-teal/20 bg-teal/[0.05] p-5">
                    <div className="text-xs font-mono uppercase tracking-[0.15em] text-teal mb-2">
                      1st-Party
                    </div>
                    <div className="font-display text-4xl font-semibold">
                      <CountUp end={4.3} decimals={1} suffix="M" />
                    </div>
                    <div className="text-xs text-muted-foreground mt-2">
                      Proprietary, verified records
                    </div>
                  </div>
                </Reveal>
                <Reveal delay={340}>
                  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
                    <div className="text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-2">
                      3rd-Party
                    </div>
                    <div className="font-display text-4xl font-semibold">
                      <CountUp end={41.7} decimals={1} suffix="M" />
                    </div>
                    <div className="text-xs text-muted-foreground mt-2">
                      Enriched via intent partners
                    </div>
                  </div>
                </Reveal>
              </div>

              <div className="mt-8">
                <div className="text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-3">
                  Intent partners
                </div>
                <div className="flex flex-wrap gap-2">
                  {INTENT_PARTNERS.map((p, i) => (
                    <Reveal key={p} delay={i * 90 + 300} y={12}>
                      <span className="rounded-full border border-white/10 px-3 py-1 text-xs font-mono">
                        {p}
                      </span>
                    </Reveal>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-5 space-y-4">
            {KEYSTATS.map((s, i) => (
              <Reveal key={s.l} delay={i * 120}>
                <div className="rounded-2xl border border-white/[0.06] bg-[#0A121A] p-6 card-hover">
                  <div className="font-display text-4xl font-semibold">
                    <CountUp
                      end={s.v}
                      decimals={s.decimals}
                      suffix={s.suffix}
                    />
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{s.l}</div>
                </div>
              </Reveal>
            ))}

            <Reveal delay={500}>
              <div className="rounded-2xl border border-teal/20 bg-teal/[0.04] p-5 flex items-center gap-3">
                <span className="h-2 w-2 rounded-full bg-teal pulse-dot" />
                <div>
                  <div className="text-xs font-mono uppercase tracking-[0.15em] text-teal">LIVE</div>
                  <div className="text-sm text-muted-foreground">
                    Data pipeline refreshed continuously · 96.1% accuracy
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Audience mix with animated bars */}
        <Reveal className="mt-14">
          <div className="rounded-2xl border border-white/[0.06] bg-[#0A121A] p-8 md:p-10">
            <div className="text-xs font-mono uppercase tracking-[0.18em] text-teal mb-2">
              Decision-Maker Audience Mix
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-6">
              {AUDIENCE.map((a, i) => (
                <div key={a.l}>
                  <div className="font-display text-4xl md:text-5xl font-semibold">
                    <CountUp end={a.v} suffix="%" />
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">{a.l}</div>
                  <AnimatedBar
                    target={`${a.v}%`}
                    delay={i * 120 + 300}
                    className="mt-3"
                  />
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>

      <style>{`
        @keyframes radarPulse {
          0% { transform: translate(-50%, -50%) scale(0.6); opacity: 0.55; }
          80% { opacity: 0.05; }
          100% { transform: translate(-50%, -50%) scale(1.1); opacity: 0; }
        }
        .radar-ring { animation: radarPulse 4.2s ease-out infinite; }

        @keyframes sweep {
          0% { transform: translateX(-100%); opacity: 0; }
          40% { opacity: 0.7; }
          100% { transform: translateX(120%); opacity: 0; }
        }
        .data-sweep {
          background: linear-gradient(90deg, transparent 0%, rgba(0,229,194,0.14) 40%, rgba(0,229,194,0.28) 50%, rgba(0,229,194,0.14) 60%, transparent 100%);
          filter: blur(6px);
          animation: sweep 9s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
}
